import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
  Row,
  Col,
  FormFeedback,
} from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import ScheduleMatrixTimeInput from "./schedule-matrix-time-input";
import {
  clampScheduleTimeValue,
  normalizeTimeValue,
} from "./schedule-matrix-time-utils";
import { AsyncClientSelect } from "components/Common/AsyncSearchSelects";
import { allowOnlyNumber } from "helpers/GeneralHelper";
import {
  applyClientAsyncFilterOptionToFormState,
  buildClientAsyncFilterInitialOption,
  getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import { updateAIScheduleMatrix, addAIScheduleMatrix } from "model/AIScheduleMatrixModel";
import { getCommonScheduleMatrixGroupOptions } from "model/ClientRegisterModel";
import { submitAISmsTemplateFilterData } from "model/AISmsTemplateModel";

const CLIENT_LABEL_FIELD = getClientAsyncFilterLabelFieldKey();

export const AI_SCHEDULE_ACTION_TYPE = {
  CALL: 1,
  SMS: 2,
};

export const SCHEDULE_PERIOD_TYPE = {
  DAY: "day",
  WEEK: "week",
};

/** API `scheduleMode`: 1 = day-based, 2 = week-day-based. */
export const SCHEDULE_MODE = {
  DAY: 1,
  WEEK: 2,
};

export const SCHEDULE_PERIOD_OPTIONS = [
  { value: SCHEDULE_PERIOD_TYPE.DAY, label: "Day" },
  { value: SCHEDULE_PERIOD_TYPE.WEEK, label: "Week" },
];

export const DAY_NO_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({
  value: String(n),
  label: String(n),
}));

export const WEEK_DAY_NO_OPTIONS = [
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
  { value: "7", label: "Sunday" },
];

export const ACTION_TYPE_OPTIONS = [
  { value: AI_SCHEDULE_ACTION_TYPE.CALL, label: "Call" },
  { value: AI_SCHEDULE_ACTION_TYPE.SMS, label: "SMS" },
];

const isBlankClientId = (value) => {
  if (value === null || value === undefined || value === "") return true;
  return String(value).trim() === "-1";
};

export {
  clampScheduleTimeValue,
  normalizeTimeValue,
} from "./schedule-matrix-time-utils";
export { default as ScheduleMatrixTimeInput } from "./schedule-matrix-time-input";

export const selectMenuStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const emptyForm = {
  groupId: "",
  stageId: "",
  attemptNo: "",
  dayNo: "",
  weekDayNo: "",
  slot: "",
  time: "",
  clientId: "",
  [CLIENT_LABEL_FIELD]: "",
  actionTypeId: "",
  aiSmsTemplateId: "",
  aiSmsTemplateName: "",
  schedulePeriod: SCHEDULE_PERIOD_TYPE.DAY,
};

const toNumberOrZero = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const toOptionalNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const pickRowValue = (row = {}, keys = []) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== null && value !== undefined && value !== "") return value;
  }
  return "";
};

const mapSmsTemplateOptions = (list = []) =>
  (Array.isArray(list) ? list : [])
    .map((item) => {
      const value = item?.id ?? item?.Id;
      if (value == null || value === "") return null;
      const label =
        String(item?.note ?? "").trim() ||
        String(item?.name ?? item?.Name ?? "").trim() ||
        String(value);
      return { value, label };
    })
    .filter(Boolean);

const normalizeCommonList = (response) =>
  response?.data?.records || response?.data?.data || response?.data || response || [];

const mapScheduleMatrixGroupOptions = (response) =>
  (Array.isArray(normalizeCommonList(response)) ? normalizeCommonList(response) : [])
    .map((item) => {
      const value = String(item?.id ?? item?.Id ?? item?.value ?? "").trim();
      const label = String(item?.display ?? item?.name ?? item?.label ?? value).trim();
      if (!value) return null;
      return { value, label: label || value };
    })
    .filter(Boolean);

const AIScheduleMatrixAddEditModal = ({
  isOpen,
  onClose,
  itemId,
  editableRow,
  defaultGroupId = "",
  onSuccess,
}) => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [smsTemplateOptions, setSmsTemplateOptions] = useState([]);
  const [scheduleMatrixGroupOptions, setScheduleMatrixGroupOptions] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);

  const isSmsAction = useMemo(
    () => Number(formData?.actionTypeId) === AI_SCHEDULE_ACTION_TYPE.SMS,
    [formData?.actionTypeId]
  );

  const loadSmsTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await submitAISmsTemplateFilterData({});
      const responseData = response?.data;
      const listData = Array.isArray(responseData)
        ? responseData
        : responseData?.records ?? [];
      setSmsTemplateOptions(mapSmsTemplateOptions(listData));
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadScheduleMatrixGroups = async () => {
    setLoadingGroups(true);
    try {
      const response = await getCommonScheduleMatrixGroupOptions();
      setScheduleMatrixGroupOptions(mapScheduleMatrixGroupOptions(response));
    } finally {
      setLoadingGroups(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const row = editableRow && typeof editableRow === "object" ? editableRow : {};
    const resolvedGroupId = String(
      pickRowValue(row, ["groupId", "GroupId", "scheduleMatrixGroupId", "ScheduleMatrixGroupId"]) ||
        (!itemId ? defaultGroupId : "")
    ).trim();
    const rawClientId = pickRowValue(row, ["clientId"]);
    const clientId = isBlankClientId(rawClientId) ? "" : String(rawClientId);
    const clientLabel = isBlankClientId(rawClientId)
      ? ""
      : String(
          pickRowValue(row, [
            CLIENT_LABEL_FIELD,
            "clientName",
            "clientTitle",
            "client",
            "ClientName",
          ]) ?? ""
        );
    const aiSmsTemplateId = pickRowValue(row, ["aiSmsTemplateId"]) ?? "";
    const aiSmsTemplateName = String(
      pickRowValue(row, ["aiSmsTemplateName", "aiSmsTemplateNote", "note"]) ?? ""
    );
    const schedulePeriod =
      row?.weekDayNo === 0 || row?.weekDayNo
        ? SCHEDULE_PERIOD_TYPE.WEEK
        : SCHEDULE_PERIOD_TYPE.DAY;
    setFormData({
      groupId: resolvedGroupId,
      stageId:
        row?.stageId === 0 || row?.stageId
          ? String(row.stageId)
          : String(pickRowValue(row, ["stageId"]) ?? ""),
      attemptNo:
        row?.attemptNo === 0 || row?.attemptNo
          ? String(row.attemptNo)
          : String(pickRowValue(row, ["attemptNo"]) ?? ""),
      dayNo:
        row?.dayNo === 0 || row?.dayNo
          ? String(row.dayNo)
          : "",
      weekDayNo:
        row?.weekDayNo === 0 || row?.weekDayNo
          ? String(row.weekDayNo)
          : "",
      slot: String(pickRowValue(row, ["slot"]) ?? ""),
      time: normalizeTimeValue(pickRowValue(row, ["time"])),
      clientId,
      [CLIENT_LABEL_FIELD]: clientLabel,
      actionTypeId: pickRowValue(row, ["actionTypeId"]) ?? "",
      aiSmsTemplateId,
      aiSmsTemplateName,
      schedulePeriod,
    });
    setErrors({});
    loadSmsTemplates();
    loadScheduleMatrixGroups();
  }, [isOpen, editableRow, itemId, defaultGroupId]);

  const closeModal = () => {
    setErrors({});
    onClose?.();
  };

  const handleNumberChange = (field, rawValue) => {
    const digitsOnly = String(allowOnlyNumber(rawValue ?? "")).replace(/\./g, "");
    setFormData((prev) => ({ ...prev, [field]: digitsOnly }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleTextChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value ?? "" }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleGroupChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      groupId: selectedOption?.value ? String(selectedOption.value) : "",
    }));
    if (errors?.groupId) {
      setErrors((prev) => ({ ...prev, groupId: "" }));
    }
  };

  const handleClientChange = (selectedOption) => {
    setFormData((prev) =>
      applyClientAsyncFilterOptionToFormState(prev, selectedOption)
    );
    if (errors?.clientId) {
      setErrors((prev) => ({ ...prev, clientId: "" }));
    }
  };

  const handleActionTypeChange = (selectedOption) => {
    const nextValue = selectedOption?.value ?? "";
    const keepSms = Number(nextValue) === AI_SCHEDULE_ACTION_TYPE.SMS;
    setFormData((prev) => ({
      ...prev,
      actionTypeId: nextValue,
      aiSmsTemplateId: keepSms ? prev.aiSmsTemplateId : "",
      aiSmsTemplateName: keepSms ? prev.aiSmsTemplateName : "",
    }));
    setErrors((prev) => ({
      ...prev,
      actionTypeId: "",
      aiSmsTemplateId: "",
    }));
  };

  const handleSmsTemplateChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      aiSmsTemplateId: selectedOption?.value ?? "",
      aiSmsTemplateName: selectedOption?.label ?? "",
    }));
    if (errors?.aiSmsTemplateId) {
      setErrors((prev) => ({ ...prev, aiSmsTemplateId: "" }));
    }
  };

  const handleSchedulePeriodChange = (selectedOption) => {
    const nextPeriod = selectedOption?.value ?? SCHEDULE_PERIOD_TYPE.DAY;
    setFormData((prev) => ({
      ...prev,
      schedulePeriod: nextPeriod,
      dayNo: nextPeriod === SCHEDULE_PERIOD_TYPE.DAY ? prev.dayNo : "",
      weekDayNo: nextPeriod === SCHEDULE_PERIOD_TYPE.WEEK ? prev.weekDayNo : "",
    }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!String(formData?.groupId ?? "").trim()) {
      nextErrors.groupId = "Schedule matrix group is required";
    }
    ["stageId", "attemptNo"].forEach((field) => {
      if (formData?.[field] === "" || formData?.[field] == null) {
        nextErrors[field] = "Required";
      }
    });
    if (formData?.actionTypeId === "" || formData?.actionTypeId == null) {
      nextErrors.actionTypeId = "Action type is required";
    }
    if (
      Number(formData?.actionTypeId) === AI_SCHEDULE_ACTION_TYPE.SMS &&
      (formData?.aiSmsTemplateId === "" || formData?.aiSmsTemplateId == null)
    ) {
      nextErrors.aiSmsTemplateId = "SMS template is required";
    }
    if (formData?.schedulePeriod === SCHEDULE_PERIOD_TYPE.DAY && !formData?.dayNo) {
      nextErrors.dayNo = "Day is required";
    }
    if (
      formData?.schedulePeriod === SCHEDULE_PERIOD_TYPE.WEEK &&
      !formData?.weekDayNo
    ) {
      nextErrors.weekDayNo = "Week day is required";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;

    const isDay = formData?.schedulePeriod === SCHEDULE_PERIOD_TYPE.DAY;
    const payload = {
      groupId: String(formData.groupId ?? "").trim(),
      stageId: toNumberOrZero(formData.stageId),
      attemptNo: toNumberOrZero(formData.attemptNo),
      dayNo: isDay ? toOptionalNumber(formData.dayNo) : null,
      weekDayNo: isDay ? null : toOptionalNumber(formData.weekDayNo),
      slot: String(formData.slot ?? ""),
      time: normalizeTimeValue(formData.time) || "00:00:00",
      clientId: isBlankClientId(formData.clientId)
        ? "-1"
        : String(formData.clientId ?? ""),
      actionTypeId: toNumberOrZero(formData.actionTypeId),
      aiSmsTemplateId:
        Number(formData.actionTypeId) === AI_SCHEDULE_ACTION_TYPE.SMS
          ? formData.aiSmsTemplateId || null
          : null,
    };

    setSaving(true);
    try {
      const response = itemId
        ? await updateAIScheduleMatrix(itemId, payload)
        : await addAIScheduleMatrix(payload);
      if (response?.success === false) {
        toast.error(
          response?.message ||
            (itemId ? "Unable to update" : "Unable to create")
        );
        return;
      }
      toast.success(
        response?.message ||
          (itemId ? "Updated successfully" : "Created successfully")
      );
      closeModal();
      onSuccess?.();
    } finally {
      setSaving(false);
    }
  };

  const actionTypeValue =
    ACTION_TYPE_OPTIONS.find(
      (opt) => String(opt.value) === String(formData?.actionTypeId ?? "")
    ) || null;

  const scheduleMatrixGroupValue =
    scheduleMatrixGroupOptions.find(
      (opt) => String(opt.value) === String(formData?.groupId ?? "")
    ) || null;

  const schedulePeriodValue =
    SCHEDULE_PERIOD_OPTIONS.find(
      (opt) => opt.value === (formData?.schedulePeriod ?? SCHEDULE_PERIOD_TYPE.DAY)
    ) || SCHEDULE_PERIOD_OPTIONS[0];

  const dayNoValue =
    DAY_NO_OPTIONS.find(
      (opt) => String(opt.value) === String(formData?.dayNo ?? "")
    ) || null;

  const weekDayNoValue =
    WEEK_DAY_NO_OPTIONS.find(
      (opt) => String(opt.value) === String(formData?.weekDayNo ?? "")
    ) || null;

  const smsTemplateValue = useMemo(() => {
    const matched = smsTemplateOptions.find(
      (opt) => String(opt.value) === String(formData?.aiSmsTemplateId ?? "")
    );
    if (matched) return matched;
    if (formData?.aiSmsTemplateId && formData?.aiSmsTemplateName) {
      return {
        value: formData.aiSmsTemplateId,
        label: formData.aiSmsTemplateName,
      };
    }
    return null;
  }, [
    smsTemplateOptions,
    formData?.aiSmsTemplateId,
    formData?.aiSmsTemplateName,
  ]);

  const smsSelectOptions = useMemo(() => {
    if (
      !formData?.aiSmsTemplateId ||
      !formData?.aiSmsTemplateName ||
      smsTemplateOptions.some(
        (opt) => String(opt.value) === String(formData.aiSmsTemplateId)
      )
    ) {
      return smsTemplateOptions;
    }
    return [
      {
        value: formData.aiSmsTemplateId,
        label: formData.aiSmsTemplateName,
      },
      ...smsTemplateOptions,
    ];
  }, [
    smsTemplateOptions,
    formData?.aiSmsTemplateId,
    formData?.aiSmsTemplateName,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      backdrop="static"
      size="lg"
      className="modal-full-height"
      centered
    >
      <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0 text-uppercase">
            {itemId ? "Edit" : "Create"} AI Schedule Matrix
          </h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>
                  Schedule Matrix Group <span className="text-danger">*</span>
                </Label>
                <div className={errors.groupId ? "is-invalid" : ""}>
                  <Select
                    options={scheduleMatrixGroupOptions}
                    className={`select2-selection${errors.groupId ? " is-invalid" : ""}`}
                    placeholder={
                      loadingGroups
                        ? "Loading schedule matrix groups..."
                        : "Select Schedule Matrix Group"
                    }
                    isLoading={loadingGroups}
                    value={scheduleMatrixGroupValue}
                    onChange={handleGroupChange}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    styles={selectMenuStyles}
                    isDisabled={true}
                  />
                </div>
                {errors.groupId ? (
                  <FormFeedback className="d-block">{errors.groupId}</FormFeedback>
                ) : null}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>
                  Stage Id <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formData.stageId}
                  invalid={!!errors.stageId}
                  onChange={(e) => handleNumberChange("stageId", e.target.value)}
                />
                {errors.stageId ? (
                  <FormFeedback className="d-block">{errors.stageId}</FormFeedback>
                ) : null}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>
                  Attempt No <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formData.attemptNo}
                  invalid={!!errors.attemptNo}
                  onChange={(e) => handleNumberChange("attemptNo", e.target.value)}
                />
                {errors.attemptNo ? (
                  <FormFeedback className="d-block">{errors.attemptNo}</FormFeedback>
                ) : null}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>
                  Action Type <span className="text-danger">*</span>
                </Label>
                <Select
                  options={ACTION_TYPE_OPTIONS}
                  className="select2-selection"
                  placeholder="Select action type"
                  value={actionTypeValue}
                  onChange={handleActionTypeChange}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  styles={selectMenuStyles}
                />
                {errors.actionTypeId ? (
                  <FormFeedback className="d-block">{errors.actionTypeId}</FormFeedback>
                ) : null}
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>
                  Type <span className="text-danger">*</span>
                </Label>
                <Select
                  options={SCHEDULE_PERIOD_OPTIONS}
                  className="select2-selection"
                  placeholder="Day / Week"
                  value={schedulePeriodValue}
                  onChange={handleSchedulePeriodChange}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  styles={selectMenuStyles}
                />
              </FormGroup>
            </Col>
            {formData?.schedulePeriod === SCHEDULE_PERIOD_TYPE.DAY ? (
              <Col md={3}>
                <FormGroup>
                  <Label>
                    Day <span className="text-danger">*</span>
                  </Label>
                  <Select
                    options={DAY_NO_OPTIONS}
                    className={`select2-selection${errors.dayNo ? " is-invalid" : ""}`}
                    placeholder="Select day"
                    value={dayNoValue}
                    onChange={(opt) => {
                      setFormData((prev) => ({ ...prev, dayNo: opt?.value ?? "" }));
                      if (errors?.dayNo) {
                        setErrors((prev) => ({ ...prev, dayNo: "" }));
                      }
                    }}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    styles={selectMenuStyles}
                  />
                  {errors.dayNo ? (
                    <FormFeedback className="d-block">{errors.dayNo}</FormFeedback>
                  ) : null}
                </FormGroup>
              </Col>
            ) : (
              <Col md={3}>
                <FormGroup>
                  <Label>
                    Week Day <span className="text-danger">*</span>
                  </Label>
                  <Select
                    options={WEEK_DAY_NO_OPTIONS}
                    className={`select2-selection${errors.weekDayNo ? " is-invalid" : ""}`}
                    placeholder="Select week day"
                    value={weekDayNoValue}
                    onChange={(opt) => {
                      setFormData((prev) => ({ ...prev, weekDayNo: opt?.value ?? "" }));
                      if (errors?.weekDayNo) {
                        setErrors((prev) => ({ ...prev, weekDayNo: "" }));
                      }
                    }}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    styles={selectMenuStyles}
                  />
                  {errors.weekDayNo ? (
                    <FormFeedback className="d-block">{errors.weekDayNo}</FormFeedback>
                  ) : null}
                </FormGroup>
              </Col>
            )}
            <Col md={6}>
              <FormGroup>
                <Label>Slot</Label>
                <Input
                  type="text"
                  value={formData.slot}
                  onChange={(e) => handleTextChange("slot", e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Time</Label>
                <ScheduleMatrixTimeInput
                  className="form-control"
                  value={formData.time || ""}
                  onChange={(next) =>
                    handleTextChange("time", clampScheduleTimeValue(next) || next)
                  }
                />
              </FormGroup>
            </Col>
            {isSmsAction ? (
              <Col md={6}>
                <FormGroup>
                  <Label>
                    Choose SMS Template <span className="text-danger">*</span>
                  </Label>
                  <Select
                    options={smsSelectOptions}
                    className="select2-selection"
                    placeholder={
                      loadingTemplates
                        ? "Loading templates..."
                        : "Choose SMS template"
                    }
                    isLoading={loadingTemplates}
                    value={smsTemplateValue}
                    onChange={handleSmsTemplateChange}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    styles={selectMenuStyles}
                  />
                  {errors.aiSmsTemplateId ? (
                    <FormFeedback className="d-block">
                      {errors.aiSmsTemplateId}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
              </Col>
            ) : null}
          </Row>

          <div className="text-end mt-3">
            <Button color="light" className="me-2" type="button" onClick={closeModal}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default AIScheduleMatrixAddEditModal;
